import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MapPin, User as UserIcon, Target, ArrowRight } from 'lucide-react';
import NamusImage from '@/components/NamusImage';
import FlagMatchButton from '@/components/FlagMatchButton';
import CommunityResearchNotes from '@/components/CommunityResearchNotes';
import { getProxiedPhotoUrl } from '@/lib/api';

function tierBadgeClass(score) {
  if (score >= 70) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
  if (score >= 45) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
  if (score >= 25) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
  return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
}

function MatchRow({ match, currentCaseId }) {
  const score = Math.round(match.match_score ?? 0);
  // Pick the "other side" of the match — i.e. NOT the current case
  const isMP = match.missing_person_id === currentCaseId;
  const otherId = isMP ? match.unidentified_person_id : match.missing_person_id;
  const otherName =
    (isMP ? match.unidentified_person_name : match.missing_person_name) || 'Unknown';
  const otherState = isMP ? match.unidentified_person_state : match.missing_person_state;
  const otherCity = isMP ? match.unidentified_person_city : match.missing_person_city;
  const otherAge = isMP ? match.unidentified_person_age : match.missing_person_age;
  // Fall back to UID age range when single age is null
  const otherAgeMin = isMP ? match.unidentified_person_age_min : null;
  const otherAgeMax = isMP ? match.unidentified_person_age_max : null;
  const ageDisplay =
    otherAge != null
      ? `Age ${otherAge}`
      : otherAgeMin != null && otherAgeMax != null
      ? `Age ${otherAgeMin}–${otherAgeMax}`
      : null;
  // Fall back to UID year of death / year found when no date available
  const otherYear = isMP
    ? match.unidentified_person_year_found ||
      match.unidentified_person_estimated_year_of_death
    : null;
  const photo =
    (isMP
      ? match.unidentified_person_photo_url
      : match.missing_person_photo_url) ??
    match.other_photo_url ??
    match.photo_url ??
    null;
  const reasons = (match.match_reasons || []).slice(0, 2);

  // Compose location string with helpful fallbacks
  const locationBits = [otherCity, otherState].filter(Boolean);
  const locationLabel = locationBits.length > 0 ? locationBits.join(', ') : null;

  return (
    <div
      data-testid={`workbench-match-${match.id}`}
      className="rounded-lg border bg-white dark:bg-slate-900/40 p-3 hover:border-indigo-300 transition-colors"
    >
      <div className="flex items-start gap-3">
        {/* Thumbnail */}
        <Link
          to={otherId ? `/case/${otherId}` : '#'}
          className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-md overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center"
        >
          {photo ? (
            <NamusImage
              src={getProxiedPhotoUrl(photo) || photo}
              alt={otherName}
              className="w-full h-full object-cover"
            />
          ) : (
            <UserIcon className="w-6 h-6 text-slate-400" />
          )}
        </Link>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <Link
              to={otherId ? `/case/${otherId}` : '#'}
              className="font-medium text-sm hover:underline truncate"
            >
              {otherName}
            </Link>
            <Badge variant="secondary" className={`text-xs shrink-0 ${tierBadgeClass(score)}`}>
              {score}/100
            </Badge>
          </div>
          <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 mb-2">
            {locationLabel && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {locationLabel}
              </span>
            )}
            {ageDisplay && (
              <span className="inline-flex items-center gap-1">
                <UserIcon className="w-3 h-3" />
                {ageDisplay}
              </span>
            )}
            {otherYear && (
              <span className="inline-flex items-center gap-1">
                {isMP ? 'Found' : 'Missing'} {otherYear}
              </span>
            )}
          </div>
          {reasons.length > 0 && (
            <ul className="text-xs text-foreground/80 space-y-0.5 mb-2">
              {reasons.map((r, i) => (
                <li key={i} className="line-clamp-1">
                  <span className="text-emerald-600">✓</span>{' '}
                  {String(r).length > 90 ? String(r).slice(0, 90) + '…' : r}
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between gap-2">
            <Link
              to={`/comparison/${match.id}`}
              className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1"
              data-testid={`workbench-compare-${match.id}`}
            >
              Side-by-side <ArrowRight className="w-3 h-3" />
            </Link>
            <FlagMatchButton matchId={match.id} size="sm" variant="outline" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CaseWorkbenchTab({ caseData, caseId, matches = [] }) {
  const top = (matches || [])
    .slice()
    .sort((a, b) => (b.match_score ?? 0) - (a.match_score ?? 0))
    .slice(0, 10);
  // Resolve "currentCaseId" — the side of the match that IS this case
  const currentCaseId =
    caseData?.id ||
    caseData?.external_id ||
    caseId;

  return (
    <div className="space-y-6" data-testid="case-workbench-tab">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2 text-base sm:text-lg">
            <Target className="w-5 h-5 text-indigo-500" />
            Top Matches — Investigation Hub
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            The 10 strongest candidates the engine has identified. Flag any
            match that warrants a closer look from our review team.
          </p>
        </CardHeader>
        <CardContent>
          {top.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              No ranked matches yet for this case.
            </p>
          ) : (
            <div className="space-y-3" data-testid="workbench-match-list">
              {top.map((m) => (
                <MatchRow key={m.id} match={m} currentCaseId={currentCaseId} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <CommunityResearchNotes caseId={currentCaseId} />
    </div>
  );
}
